import React, { useCallback } from "react";

import DateRangeType from "@mapmycustomers/shared/enum/DateRangeType";
import { StandardRange } from "@mapmycustomers/shared/types/range/DateRange";

import CustomRangePicker from "@app/component/input/dateRangeSelect/components/CustomRangePicker";
import DaySelect from "@app/component/input/dateRangeSelect/components/DaySelect";
import MonthSelect from "@app/component/input/dateRangeSelect/components/MonthSelect";
import QuarterSelect from "@app/component/input/dateRangeSelect/components/QuarterSelect";
import WeekSelect from "@app/component/input/dateRangeSelect/components/WeekSelect";
import DateRange from "@app/scene/reports/types/DateRange";
import parseRange from "@app/util/range/parseRange";

interface Props {
  className?: string;
  dateRange: DateRange | undefined;
  dateRangeType: DateRangeType.ALL | DateRangeType.CUSTOM | StandardRange;
  isCustomDateDisabled?: (date: Date) => boolean;
  label?: React.ReactNode;
  onChangeDateRange?: (value: DateRange) => void;
  onChangeDateRangeType?: (value: StandardRange) => void;
}

const Range: React.FC<Props> = ({
  className,
  dateRange,
  dateRangeType,
  isCustomDateDisabled,
  label,
  onChangeDateRange,
  onChangeDateRangeType,
}) => {
  const result = parseRange(dateRangeType) ?? {
    dateRangeType: DateRangeType.WEEK,
    offset: 0,
  };

  const handleChange = useCallback(
    (value?: StandardRange) => {
      // we know value will be always defined because we don't use "allowClear" prop, but
      // still need to ensure...
      if (value && onChangeDateRangeType) {
        onChangeDateRangeType(value);
      }
    },
    [onChangeDateRangeType]
  );

  switch (result.dateRangeType) {
    case DateRangeType.ALL:
      return null;
    case DateRangeType.DAY:
      return (
        <DaySelect
          className={className}
          label={label}
          onChange={handleChange}
          value={`${result.dateRangeType}${result.offset}`}
        />
      );
    case DateRangeType.WEEK:
      return (
        <WeekSelect
          className={className}
          label={label}
          onChange={handleChange}
          value={`${result.dateRangeType}${result.offset}`}
        />
      );
    case DateRangeType.MONTH:
      return (
        <MonthSelect
          className={className}
          label={label}
          onChange={handleChange}
          value={`${result.dateRangeType}${result.offset}`}
        />
      );
    case DateRangeType.QUARTER:
      return (
        <QuarterSelect
          className={className}
          label={label}
          onChange={handleChange}
          value={`${result.dateRangeType}${result.offset}`}
        />
      );
    case DateRangeType.CUSTOM:
      return (
        <CustomRangePicker
          className={className}
          isDateDisabled={isCustomDateDisabled}
          label={label}
          onChange={onChangeDateRange}
          value={dateRange}
        />
      );
  }
};

export default Range;
